import type { TeamMembership } from '@rhythmic/db/types'
import type { CreateTeamMembershipPayload } from '@rhythmic/types'
import { CreateTeamMembershipSchema, TeamMembershipSchema } from '@rhythmic/types'
import type { Transaction } from '@tanstack/vue-db'
import { useRhythmicLogger } from '#imports'

import { getTeamMembershipCollection } from '../../getCollections'

const logger = useRhythmicLogger('useTeamContext:createTeamMembership')

export interface CreateTeamMembershipContext {
  teamId: string
}

function validateParams(payload: CreateTeamMembershipPayload, context: CreateTeamMembershipContext) {
  const parsed = CreateTeamMembershipSchema.parse(payload)
  const { teamId } = context

  if (!teamId) {
    const error = new Error('Team context is not ready')
    logger.error('Unable to create team membership: missing team identifier')
    throw error
  }

  if (parsed.team_id !== teamId) {
    const error = new Error('Payload team identifier does not match active team context')
    logger.error('Unable to create team membership: mismatched team identifier', {
      payloadTeamId: parsed.team_id,
      contextTeamId: teamId,
    })
    throw error
  }

  return {
    teamId,
    userId: parsed.user_id,
    permissions: parsed.permissions,
  }
}

export default function handleCreateTeamMembership(
  payload: CreateTeamMembershipPayload,
  context: CreateTeamMembershipContext,
): Transaction<TeamMembership> {
  try {
    const { teamId, userId } = validateParams(payload, context)
    const collection = getTeamMembershipCollection(teamId)

    const membership = TeamMembershipSchema.parse(payload)
    logger.debug('Creating team membership via collection', { membership })

    const transaction = collection.insert(membership) as Transaction<TeamMembership>
    logger.info('Team membership created via collection', {
      teamId,
      userId,
    })

    return transaction
  } catch (error) {
    logger.error('createTeamMembership failed', { payload, error })
    if (error instanceof Error) throw error
    throw new Error('Failed to create team membership')
  }
}
